import * as React from 'react'
import classNames from 'classnames'

import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { ExecutionElement } from '../flow/ExecutionElement'
import { ElementLabel } from './ElementLabel'

import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import { SVG_Cross_WithCircle } from '../svg/SVG_Cross_WithCircle'
import { SVG_Tick_WithCircle } from '../svg/SVG_Tick_WithCircle'
import type { TimelineDetails } from './TimelineDetails'
import i18next from 'i18next'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'

interface Props {
    deps: CommonDeps
    elementId: Flows.ElementId
    loopPathIds: LoopPathIds
    label: string
    timelineDetails: TimelineDetails
    required: boolean
    answerValue: Flows.YesNoAnswer['value']
    validationMessages: string[]
    createAnswer: CreateAnswer<Flows.YesNoAnswer>
    useIcons: boolean
    yesWeight: number
    noWeight: number
    iconSize: Flows.YesNoElement['iconSize']
    openOptions?: () => void
}

export class ElementYesNo extends React.Component<Props, {}> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    private renderWithIcons = () => {
        const { createAnswer, answerValue, yesWeight, noWeight } = this.props

        return (
            <div
                className={classNames(
                    'AppCore__flow__ElementYesNo',
                    { withIcon: true, [`${this.props.iconSize}IconSize`]: true },
                    { editing: this.props.timelineDetails !== null }
                )}
            >
                <div className="AppCore__flow__ElementYesNo__icons">
                    <div
                        className={classNames('AppCore__flow__ElementYesNo__icon', {
                            selected: answerValue === 'yes',
                            yes: true,
                        })}
                        onClick={() =>
                            createAnswer((base) => ({
                                ...base,
                                type: 'yes-no',
                                value: answerValue !== 'yes' ? 'yes' : null,
                            }))
                        }
                    >
                        <SVG_Tick_WithCircle
                            className="AppCore__flow__ElementYesNo__icon__svg"
                            color={answerValue === 'no' ? '#C2E5DA' : null}
                        />
                        <span
                            className={classNames('AppCore__flow__ElementYesNo__icon__weight', {
                                faded: answerValue === 'no',
                            })}
                        >
                            {yesWeight}
                        </span>
                    </div>

                    <div
                        className={classNames('AppCore__flow__ElementYesNo__icon', {
                            selected: answerValue === 'no',
                            no: true,
                        })}
                        onClick={() =>
                            createAnswer((base) => ({
                                ...base,
                                type: 'yes-no',
                                value: answerValue !== 'no' ? 'no' : null,
                            }))
                        }
                    >
                        <SVG_Cross_WithCircle
                            className="AppCore__flow__ElementYesNo__icon__svg"
                            color={answerValue === 'yes' ? '#EDCBD6' : null}
                        />
                        <span
                            className={classNames('AppCore__flow__ElementYesNo__icon__weight', {
                                faded: answerValue === 'yes',
                            })}
                        >
                            {noWeight}
                        </span>
                    </div>
                </div>
            </div>
        )
    }

    private renderWithText = () => {
        const { createAnswer, answerValue } = this.props

        return (
            <div className="AppCore__flow__ElementYesNo">
                <div
                    className={classNames('AppCore__flow__ElementYesNo__button', 'AppCore__flow__ElementYesNo__yes', {
                        selected: answerValue === 'yes',
                        notSelected: answerValue === 'no',
                        error: this.props.validationMessages.length !== 0,
                    })}
                    onClick={() =>
                        createAnswer((base) => ({
                            ...base,
                            type: 'yes-no',
                            value: answerValue !== 'yes' ? 'yes' : null,
                        }))
                    }
                >
                    <span>{i18next.t('appcore:common.yes')}</span>
                </div>
                <div
                    className={classNames('AppCore__flow__ElementYesNo__button', 'AppCore__flow__ElementYesNo__no', {
                        selected: answerValue === 'no',
                        notSelected: answerValue === 'yes',
                        error: this.props.validationMessages.length !== 0,
                    })}
                    onClick={() =>
                        createAnswer((base) => ({
                            ...base,
                            type: 'yes-no',
                            value: answerValue !== 'no' ? 'no' : null,
                        }))
                    }
                >
                    <span>{i18next.t('appcore:common.no')}</span>
                </div>
            </div>
        )
    }

    private tryRender = () => {
        const {
            deps,
            elementId,
            label,
            timelineDetails,
            required,
            validationMessages,
            useIcons,
            iconSize,
            openOptions,
        } = this.props
        return (
            <ExecutionElement
                deps={deps}
                elementId={elementId}
                loopPathIds={this.props.loopPathIds}
                validationMessages={validationMessages}
                inline={useIcons && iconSize === 'small'}
                timelineDetails={timelineDetails}
            >
                <ElementLabel
                    required={required}
                    label={label}
                    inline={useIcons}
                    hasBeenEdited={timelineDetails && timelineDetails.hasBeenEdited}
                    language={deps.language}
                    validationMessages={useIcons ? [] : validationMessages}
                    openOptions={openOptions}
                />
                {useIcons ? this.renderWithIcons() : this.renderWithText()}
            </ExecutionElement>
        )
    }
}
